import {
    Box,
    Chip,
    Drawer,
    Grid,
    IconButton,
    Stack,
    Divider,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { When } from 'react-if';

import {AlertTypes} from '../../types';
import { ComponentPermissionGateway } from '../component-permission-gateway';
import {
    customerPermissionFlags,
} from '../../constants/systemPermissions.constants';
import React, { useState } from 'react';
import { AlertPathMapComponent } from './alert-path-map-component';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import {reportStyles} from "../reports/styles";
import {AlertService} from "../../services/alert.service";
import Loading from "../loading";
import { useEffectiveSettings, SETTING } from "../../hooks/use-effective-settings";

interface Props {
    onClose(reload:boolean): void;
    visible: boolean;
    type?: string,
    alert: AlertTypes.Model;
    resolveLoading?:  boolean;
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Grid item md={4} sm={12} xs={12}>
        <Typography fontSize={13} fontWeight={500} color="text.secondary">
            {label}
        </Typography>
        <Typography fontSize={14} fontWeight={400} mt={0.5}>
            {children}
        </Typography>
    </Grid>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item md={12} sm={12} xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
            {children}
        </Typography>
    </Grid>
);

export const RiskReviewDetailsDrawer: React.FC<Props> = ({
                                                             onClose,
                                                             visible,
                                                             alert,
                                                             type,
                                                             resolveLoading
                                                         }) => {

    const findFirst = (e: unknown) => e !== undefined;
    const location = alert?.locations?.find(findFirst);

    const [showAllLocations, setShowAllLocations] = useState(false);

    const safeScoreBgColor = (score: number | string) => {
        if(!score || score === 'N/A')
            return 'grey.500';
        if (score === 5) {
            return 'success.dark';
        } else if (score === 4) {
            return 'success.light';
        } else if (score === 3) {
            return 'warning.main';
        } else {
            return 'error.main';
        }
    }

    const getRiskScore = (score: number, displayFlag : boolean) => {
        if(!displayFlag)
            return "N/A"
        else
            return score ?? "N/A"
    }

    const resolveAlert = async()=>{
        if(alert && alert.id) {
            await AlertService.resolve(alert.id, 'resolved', type!).then(() => {
                toast.success('Alert resolved successfully');
                onClose(true);
            }).catch(() => {
                toast.error('Failed to resolve alert');
            });
        }
    }

    const { getSetting, isLoading: isCustomerSettingsLoading } = useEffectiveSettings();
    const checkOnMeRiskReportSurvey = getSetting(SETTING.checkOnMeRiskReportSurvey, false);

    // Field Safety Reports share the RiskReview alert type but never carry
    // numeric survey scores — only a comment. Suppress the score boxes in
    // that case so reviewers don't see N/A clutter.
    const isFromFieldSafetyReport = /field.?safety/i.test(alert?.initiatedBy || '');

    const typeLabel = isFromFieldSafetyReport ? 'Field Safety Report' : 'Visit Risk Review';
    const statusChip: { label: string; color: ChipColor } = (() => {
        if (alert?.resolvedAt) return { label: 'Resolved', color: 'success' };
        if (alert?.status === 'pending-review') return { label: 'Pending Review', color: 'warning' };
        if (alert?.status === 'active') return { label: 'Active', color: 'warning' };
        return { label: alert?.status || 'Unknown', color: 'default' };
    })();

    return (
        <>
            <Drawer
                anchor="bottom"
                open={visible}
                onClose={onClose}
                sx={{ zIndex: '2000' }}
            >
                <Stack
                    direction={'column'}
                    justifyContent={'space-between'}
                    style={{
                        backgroundColor: '#FFF',
                    }}
                >
                    <Box sx={{ minHeight: { xs: '500px', md: '500px' } }}>
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            mt={1}
                            style={{ padding: 10 }}
                        >
                            <Typography
                                fontSize={24}
                                fontWeight={500}
                                textAlign={'left'}
                            >
                                Alert Details
                            </Typography>
                            <IconButton
                                onClick={()=>{onClose(false)}}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                        <Divider />

                        {/* Status hero — type + status, mirrors AlertDetailsDrawer */}
                        <Box sx={{ px: 2, py: 2, backgroundColor: '#FAFBFC' }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip label={typeLabel} color="info" size="small" />
                                <Chip label={statusChip.label} color={statusChip.color} size="small" variant="outlined" />
                                {alert?.resolvedAt && (
                                    <Typography fontSize={13} color="text.secondary">
                                        Resolved {dayjs(alert.resolvedAt).format('MMM D, YYYY h:mm A')}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>

                        <Divider />

                        <Box style={{ padding: 16 }} mt={1}>
                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    wordWrap: 'break-word',
                                }}
                            >
                                <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={4}
                                    sm={12}
                                    alignContent={'flex-start'}
                                >
                                    <SectionHeader>Alert</SectionHeader>
                                    <FieldRow label="Initiated By">{alert?.initiatedBy || 'N/A'}</FieldRow>
                                    <FieldRow label="Initiated At">
                                        {alert?.initiatedAt ? dayjs(alert.initiatedAt).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                    </FieldRow>
                                    <FieldRow label="Transmitter Code">{alert?.transmitterCode || 'N/A'}</FieldRow>

                                    <Grid item md={12} mt={1} sm={12} xs={12}>
                                        <Divider />
                                    </Grid>

                                    <SectionHeader>Subject</SectionHeader>
                                    <FieldRow label="Full Name">
                                        {[alert?.user?.firstName, alert?.user?.lastName].filter(Boolean).join(' ') || 'N/A'}
                                    </FieldRow>
                                    <FieldRow label="Phone Number">{alert?.user?.phoneNumber || 'N/A'}</FieldRow>
                                    <FieldRow label="Backup Email">{alert?.user?.backupEmail || 'N/A'}</FieldRow>

                                    {(alert?.resolvedAt || alert?.resolvedBy) && (
                                        <>
                                            <Grid item md={12} mt={1} sm={12} xs={12}>
                                                <Divider />
                                            </Grid>
                                            <SectionHeader>Resolution</SectionHeader>
                                            {alert?.resolvedAt && (
                                                <FieldRow label="Resolved At">
                                                    {dayjs(alert.resolvedAt).format('MM/DD/YYYY hh:mm A')}
                                                </FieldRow>
                                            )}
                                            {alert?.resolvedBy && <FieldRow label="Resolved By">{alert.resolvedBy}</FieldRow>}
                                        </>
                                    )}
                                </Grid>

                                <Divider orientation="vertical" flexItem />

                                <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={4}
                                    sm={12}
                                    xs={12}
                                    alignContent={'flex-start'}
                                >
                                    <Grid item md={12} sm={12} xs={12}>
                                        <Typography fontWeight={400}>
                                            Visit Risk Review Details
                                        </Typography>
                                        <Stack mt={4} alignItems={'center'} spacing={2}>
                                            <Typography fontWeight={600} fontSize={16}>
                                                Visit Risk Review Details
                                            </Typography>
                                            {!isFromFieldSafetyReport && (
                                                <Typography fontWeight={400} fontSize={12} color="text.secondary">
                                                    1 = very unsafe&nbsp;&nbsp;5 = very safe
                                                </Typography>
                                            )}
                                            <When condition={isCustomerSettingsLoading}>
                                                <Loading
                                                    loading={
                                                        'Loading risk report survey...'
                                                    }
                                                />
                                            </When>
                                            <When condition={!isCustomerSettingsLoading && !isFromFieldSafetyReport}>
                                                <Stack direction="row" spacing={4} justifyContent="center" sx={{ width: '100%' }}>
                                                    <Stack alignItems="center" spacing={1} maxWidth={180}>
                                                        <Typography fontWeight={500} fontSize={12} color="text.secondary" textAlign="center">
                                                            How safe did you feel in the home?
                                                        </Typography>
                                                        <Box sx={{
                                                            backgroundColor: safeScoreBgColor(getRiskScore(alert?.riskReviewSurveyHomeScore!, checkOnMeRiskReportSurvey)),
                                                            borderRadius: '8px',
                                                            minWidth: 64,
                                                            textAlign: 'center',
                                                            px: 2, py: 1.5,
                                                            color: 'primary.contrastText',
                                                        }}>
                                                            <Typography fontWeight={700} fontSize={28} lineHeight={1}>
                                                                {getRiskScore(alert?.riskReviewSurveyHomeScore!, checkOnMeRiskReportSurvey)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Stack alignItems="center" spacing={1} maxWidth={180}>
                                                        <Typography fontWeight={500} fontSize={12} color="text.secondary" textAlign="center">
                                                            How safe did you feel in the neighborhood?
                                                        </Typography>
                                                        <Box sx={{
                                                            backgroundColor: safeScoreBgColor(getRiskScore(alert?.riskReviewSurveyNeighborhoodScore!, checkOnMeRiskReportSurvey)),
                                                            borderRadius: '8px',
                                                            minWidth: 64,
                                                            textAlign: 'center',
                                                            px: 2, py: 1.5,
                                                            color: 'primary.contrastText',
                                                        }}>
                                                            <Typography fontWeight={700} fontSize={28} lineHeight={1}>
                                                                {getRiskScore(alert?.riskReviewSurveyNeighborhoodScore!, checkOnMeRiskReportSurvey)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Stack>
                                                <When condition={!checkOnMeRiskReportSurvey}>
                                                    <Typography fontWeight={500} fontSize={12} color="text.secondary">
                                                        Visit Risk Review Survey is not Enabled.
                                                    </Typography>
                                                </When>
                                            </When>
                                            {isFromFieldSafetyReport && (
                                                <Typography fontWeight={500} fontSize={12} color="text.secondary" textAlign="center">
                                                    Field Safety Report — no survey scores submitted.
                                                </Typography>
                                            )}
                                        </Stack>
                                        <When condition={alert?.riskReviewSurveyComment && alert?.riskReviewSurveyComment.length > 0}>
                                            <Box mt={3}>
                                                <Typography fontWeight={500} fontSize={11} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
                                                    Comment
                                                </Typography>
                                                <Box sx={{
                                                    borderLeft: 3,
                                                    borderColor: 'primary.main',
                                                    backgroundColor: '#F4F6F8',
                                                    borderRadius: '0 4px 4px 0',
                                                    px: 2,
                                                    py: 1.5,
                                                }}>
                                                    <Typography fontSize={15} fontWeight={400} sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {alert?.riskReviewSurveyComment}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </When>
                                        <When condition={alert?.status === "pending-review"}>
                                            <Divider sx={{mt:2}}/>
                                        </When>
                                    </Grid>
                                    <ComponentPermissionGateway
                                        role={
                                            customerPermissionFlags.TEAM_LEADER
                                        }
                                    >
                                        <When condition={alert?.status === "pending-review"}>
                                            <Grid item md={12} sm={12} xs={12} mt={4}>
                                                <Stack alignItems={'center'}>
                                                    <Typography fontWeight={400} fontSize={12}>
                                                        This Visit Risk Review is pending review. Click ‘Resolve’ to acknowledge it has been reviewed
                                                    </Typography>
                                                    <LoadingButton
                                                        sx={{
                                                            marginTop:6,
                                                            marginBottom:2,
                                                            ...reportStyles.button
                                                        }}
                                                        variant="contained"
                                                        loading={resolveLoading}
                                                        onClick={resolveAlert}
                                                        disabled={false}
                                                    >
                                                        <Typography fontWeight={700} fontSize={20} lineHeight={"24px"}>
                                                            Resolve
                                                        </Typography>
                                                    </LoadingButton>
                                                </Stack>
                                            </Grid>
                                        </When>
                                    </ComponentPermissionGateway>
                                </Grid>
                                <Divider orientation="vertical" flexItem />
                                <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={4}
                                    sm={12}
                                    xs={12}
                                    alignContent={'flex-start'}
                                >
                                    <When
                                        condition={
                                            location?.latitude &&
                                            location.longitude
                                        }
                                    >
                                        <AlertPathMapComponent
                                            showAllLocations={showAllLocations}
                                            setShowAllLocations={
                                                setShowAllLocations
                                            }
                                            location={location}
                                            alert={alert}
                                        />
                                    </When>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Stack>
            </Drawer>
        </>
    );
};